import React, { useMemo, useState } from 'react';
import {
    Card,
    Col,
    DatePicker,
    Row,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    Alert,
    Button,
    Collapse,
} from 'antd';
import { QualificationConfigEditor } from '../components/QualificationConfigEditor';
import {
    FunnelPlotOutlined,
    CheckCircleTwoTone,
    CloseCircleOutlined,
    ReloadOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useGetCrmFunnelQuery } from '../../services/nestjs-base-api';
import { ICrmFunnelOrg, ISignupFunnelOrg, ISignupFunnelStage, SignupFunnelStageKey } from '../../services/nestjs-base-api/types';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text } = Typography;

const DEFAULT_SINCE = '2026-04-01';

const daysBetween = (a: string | Date, b: string | Date): number => {
    const ms = new Date(b).getTime() - new Date(a).getTime();
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const stageTimestampField: Record<SignupFunnelStageKey, keyof ISignupFunnelOrg> = {
    registered: 'registeredAt',
    signedIn: 'signedInAt',
    onboardingComplete: 'onboardingCompletedAt',
    surveyAccessed: 'surveyAccessedAt',
    dashboardUnlocked: 'dashboardUnlockedAt',
    fullTrialUnlocked: 'fullTrialUnlockedAt',
    subscribed: 'subscribedAt',
};

const stageTagColor: Record<SignupFunnelStageKey, string> = {
    registered: 'default',
    signedIn: 'blue',
    onboardingComplete: 'cyan',
    surveyAccessed: 'geekblue',
    dashboardUnlocked: 'purple',
    fullTrialUnlocked: 'magenta',
    subscribed: 'green',
};

export const AdminSignupFunnelPage: React.FC = () => {
    const { user } = useAuth();
    const [since, setSince] = useState<string>(DEFAULT_SINCE);

    const { data, isLoading, isFetching, error, refetch } = useGetCrmFunnelQuery({
        jwtToken: user?.jwtToken || '',
        since,
    });

    const stages = data?.stages || [];
    const orgs = data?.orgs || [];
    const total = stages[0]?.count || 0;

    const stageLabelByKey = useMemo(() => {
        const m: Record<string, string> = {};
        stages.forEach((s) => { m[s.key] = s.label; });
        return m;
    }, [stages]);

    const subscribedCount = stages.find((s) => s.key === 'subscribed')?.count || 0;
    const minResponses = data?.thresholds.dashboardMinResponses ?? 10;
    const orgsWithMinResponses = orgs.filter((o) => o.responseCount >= minResponses).length;
    const fullTrialMinResponses = data?.thresholds.fullTrialMinResponses ?? 10;
    const orgsWithFullTrialResponses = orgs.filter((o) => o.responseCount >= fullTrialMinResponses).length;
    const isStalled = (o: ISignupFunnelOrg): boolean => {
        if (o.currentStage === 'subscribed' || o.currentStage === 'fullTrialUnlocked') return false;
        return daysInCurrentStage(o) >= 7;
    };
    const stalledCount = orgs.filter(isStalled).length;
    const avgDaysToSubscribe = useMemo(() => {
        const subs = orgs.filter((o) => o.subscribedAt);
        if (subs.length === 0) return null;
        const total = subs.reduce((sum, o) => sum + daysBetween(o.registeredAt, o.subscribedAt!), 0);
        return Math.round(total / subs.length);
    }, [orgs]);

    const columns: ColumnsType<ISignupFunnelOrg> = [
        {
            title: 'Organisation',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 220,
            render: (name: string, org) => (
                <Space direction="vertical" size={0}>
                    <Space size={6}>
                        <Link to={`/admin/organizations/${org.id}`}>{name}</Link>
                        {(org as ICrmFunnelOrg).crmCompanyId && (
                            <Link to={`/admin/crm?company=${(org as ICrmFunnelOrg).crmCompanyId}`}>
                                <Tag color="geekblue" style={{ marginRight: 0 }}>CRM</Tag>
                            </Link>
                        )}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {org.userCount} user{org.userCount === 1 ? '' : 's'} · {org.surveyCount} survey{org.surveyCount === 1 ? '' : 's'} · {org.responseCount} response{org.responseCount === 1 ? '' : 's'}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Registered',
            dataIndex: 'registeredAt',
            key: 'registeredAt',
            width: 140,
            sorter: (a, b) => new Date(a.registeredAt).getTime() - new Date(b.registeredAt).getTime(),
            render: (d: string) => (
                <Tooltip title={dayjs(d).format('YYYY-MM-DD HH:mm')}>
                    {dayjs(d).format('MMM D')}
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>{daysBetween(d, new Date())}d ago</Text>
                </Tooltip>
            ),
        },
        {
            title: 'Current stage',
            dataIndex: 'currentStage',
            key: 'currentStage',
            width: 220,
            filters: (stages as ISignupFunnelStage[]).map((s) => ({ text: s.label, value: s.key })),
            onFilter: (value, record) => record.currentStage === value,
            render: (key: SignupFunnelStageKey) => (
                <Tag color={stageTagColor[key]}>{stageLabelByKey[key] || key}</Tag>
            ),
        },
        {
            title: 'Days in stage',
            key: 'daysInStage',
            width: 120,
            sorter: (a, b) => daysInCurrentStage(b) - daysInCurrentStage(a),
            defaultSortOrder: 'ascend',
            render: (_, org) => {
                const days = daysInCurrentStage(org);
                const stalled = isStalled(org);
                return (
                    <Text style={{ color: stalled ? '#ff4d4f' : undefined, fontWeight: stalled ? 600 : undefined }}>
                        {days}d
                    </Text>
                );
            },
        },
    ];

    // Add one column per stage (skip registered since it's implicit)
    const stageColumns: ColumnsType<ISignupFunnelOrg> = (['signedIn', 'onboardingComplete', 'surveyAccessed', 'dashboardUnlocked', 'fullTrialUnlocked', 'subscribed'] as SignupFunnelStageKey[]).map((key) => ({
        title: stageLabelByKey[key] || key,
        key,
        width: 130,
        align: 'center',
        render: (_, org) => {
            const ts = org[stageTimestampField[key]] as string | null;
            if (!ts) return <CloseCircleOutlined style={{ color: '#d9d9d9' }} />;
            return (
                <Tooltip title={dayjs(ts).format('YYYY-MM-DD HH:mm')}>
                    <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
                        <CheckCircleTwoTone twoToneColor="#52c41a" />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            +{daysBetween(org.registeredAt, ts)}d
                        </Text>
                    </Space>
                </Tooltip>
            );
        },
    }));

    const allColumns = [...columns, ...stageColumns];

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            <FunnelPlotOutlined style={{ marginRight: 8 }} />
                            Signup Funnel
                        </Title>
                        <Text type="secondary">
                            Real (non-mock) organisations tracked from registration through subscription.
                        </Text>
                    </Col>
                    <Col>
                        <Space>
                            <Text type="secondary">Registered since:</Text>
                            <DatePicker
                                value={dayjs(since)}
                                onChange={(d: Dayjs | null) => setSince((d || dayjs(DEFAULT_SINCE)).format('YYYY-MM-DD'))}
                                allowClear={false}
                            />
                            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Collapse
                    items={[
                        {
                            key: 'qualification-config',
                            label: 'Qualification Config (signup quiz taxonomy)',
                            children: <QualificationConfigEditor />,
                        },
                    ]}
                />

                {error && (
                    <Alert type="error" message="Failed to load signup funnel" showIcon />
                )}

                <Row gutter={[16, 16]} align="stretch">
                    <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ display: 'flex' }}>
                        <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
                            <Statistic title={<StatTitle>Total cohort</StatTitle>} value={total} prefix={isLoading ? <LoadingOutlined /> : null} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ display: 'flex' }}>
                        <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
                            <Statistic
                                title={<StatTitle>{`Dashboard unlocked (≥${data?.thresholds.dashboardMinResponses ?? 10} responses)`}</StatTitle>}
                                value={orgsWithMinResponses}
                                suffix={total > 0 ? `/ ${total}` : ''}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ display: 'flex' }}>
                        <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
                            <Statistic title={<StatTitle>No progress in 7+ days (before full trial)</StatTitle>} value={stalledCount} valueStyle={{ color: stalledCount > 0 ? '#ff4d4f' : undefined }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ display: 'flex' }}>
                        <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
                            <Statistic
                                title={<StatTitle>{`Full trial unlocked (≥${fullTrialMinResponses} responses)`}</StatTitle>}
                                value={orgsWithFullTrialResponses}
                                suffix={total > 0 ? `/ ${total}` : ''}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ display: 'flex' }}>
                        <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
                            <Statistic title={<StatTitle>Subscribed</StatTitle>} value={subscribedCount} suffix={total > 0 ? `/ ${total}` : ''} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ display: 'flex' }}>
                        <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
                            <Statistic title={<StatTitle>Avg days to subscribe</StatTitle>} value={avgDaysToSubscribe ?? '—'} suffix={avgDaysToSubscribe != null ? 'd' : ''} />
                        </Card>
                    </Col>
                </Row>

                <Card title="Conversion funnel" loading={isLoading}>
                    <FunnelBars stages={stages} total={total} />
                </Card>

                <Card title={`Organisations (${orgs.length})`} loading={isLoading}>
                    <Table<ISignupFunnelOrg>
                        rowKey="id"
                        dataSource={orgs}
                        columns={allColumns}
                        pagination={{ pageSize: 50, showSizeChanger: true }}
                        scroll={{ x: 1400 }}
                        size="middle"
                    />
                </Card>
            </Space>
        </div>
    );
};

const StatTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={{ minHeight: 44, display: 'flex', alignItems: 'flex-start' }}>
        {children}
    </div>
);

function daysInCurrentStage(org: ISignupFunnelOrg): number {
    const ts = org[stageTimestampField[org.currentStage]] as string | null;
    const from = ts || org.registeredAt;
    return daysBetween(from, new Date());
}

const FunnelBars: React.FC<{ stages: ISignupFunnelStage[]; total: number }> = ({ stages, total }) => {
    if (total === 0) {
        return <Text type="secondary">No organisations in this cohort yet.</Text>;
    }
    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {stages.map((stage, idx) => {
                const pctOfTotal = Math.round((stage.count / total) * 100);
                const prev = idx > 0 ? stages[idx - 1].count : null;
                const dropoff = prev != null && prev > 0 ? Math.round(((prev - stage.count) / prev) * 100) : 0;
                const barWidth = stage.count === 0 ? 0 : Math.max(2, pctOfTotal);
                return (
                    <div key={stage.key}>
                        <Row justify="space-between" style={{ marginBottom: 4 }}>
                            <Col>
                                <Text strong>{stage.label}</Text>
                            </Col>
                            <Col>
                                <Text>{stage.count}</Text>
                                <Text type="secondary"> · {pctOfTotal}%</Text>
                                {idx > 0 && dropoff > 0 && (
                                    <Text type="danger" style={{ marginLeft: 8 }}>
                                        −{dropoff}% vs prev
                                    </Text>
                                )}
                            </Col>
                        </Row>
                        <div style={{
                            background: '#f0f2f5',
                            borderRadius: 4,
                            height: 24,
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                width: `${barWidth}%`,
                                height: '100%',
                                background: idx === stages.length - 1 ? '#52c41a' : '#18216d',
                                transition: 'width 0.3s ease',
                            }} />
                        </div>
                    </div>
                );
            })}
        </Space>
    );
};
